/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaAreasList.tsx
 * FILE_ID: JOBHUB-QA-AREAS-LIST-001
 * PURPOSE: Levels rail with their Locations, plus the add-area control.
 * LANDMINE: One unnamed Level is the common case — suppress that header.
 *      The action toolbar lives beside AREAS, not inside this list.
 */
import { useEffect, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { QaDragHandle } from '../../qa/QaDragHandle'
import type { QaAreaExecution, QaFloor } from '../../qa/qaApi'
import { normalizeList } from '../../qa/qaApiHelpers'
import { AreaRow } from '../JobQaListChips'
import { QaUndoBar } from './QaUndoBar'
import { addBlankArea } from './addBlankArea'
import { levelsOf } from './qaTabSelectors'
import { type QaUndoableMove } from './useQaTreeMoves'
import { useQaSiblingReorder } from './useQaSiblingReorder'
import type { JobQaTabController } from './useJobQaTabController'

export function JobQaAreasList({
  controller,
  openAreaId,
  onSelectArea,
  startRenameId,
  onRenameStarted,
  onOpenMenu,
  onOpenLevelMenu,
  renameFloorId,
  onRenameFloorStarted,
}: {
  controller: JobQaTabController
  openAreaId: string | null
  onSelectArea: (areaId: string) => void
  startRenameId?: string | null
  onRenameStarted?: () => void
  onOpenMenu?: (area: { id: string; area_name: string }, event: { clientX: number; clientY: number }) => void
  onOpenLevelMenu?: (floor: { id: string; floor_name: string }, event: { clientX: number; clientY: number }) => void
  renameFloorId?: string | null
  onRenameFloorStarted?: () => void
}) {
  const { selection, workspace } = controller
  const tree = controller.tree
  const moves = useQaSiblingReorder(controller)
  const areas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
  const levels = levelsOf(floors, areas)
  const defaultFloorId = floors[0]?.id
  const [editingGroup, setEditingGroup] = useState<string | null>(null)
  useEffect(() => {
    if (!renameFloorId) return
    setEditingGroup(renameFloorId)
    onRenameFloorStarted?.()
  }, [renameFloorId, onRenameFloorStarted])
  const [blankPending, setBlankPending] = useState(false)
  const addLocation = async () => {
    const jobId = controller.props?.jobId
    if (blankPending || !workspace.activeInspectionId || jobId == null) return
    setBlankPending(true)
    try {
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankArea.ts (addBlankArea)
      await addBlankArea(workspace, jobId)
    } finally {
      setBlankPending(false)
    }
  }
  return (
    <div className="h-fit rounded-xl border border-border bg-card p-2">
      {tree.undoable?.nodeType === 'area' && (
        <QaUndoBar
          message={`Moved to ${tree.undoable.parentName}.`}
          busy={tree.undo.isPending}
          onUndo={() => tree.undo.mutate(tree.undoable as QaUndoableMove)}
          onDismiss={tree.dismissUndo}
        />
      )}
      {workspace.areasQuery.isLoading ? (
        <div className="px-2 py-3 text-xs text-muted-foreground">Loading locations…</div>
      ) : areas.length === 0 && !floors.some((floor) => floor.id !== defaultFloorId) ? (
        <div className="px-2 py-3 text-xs text-muted-foreground">No locations.</div>
      ) : (
        levels.map(({ floor, areas: levelAreas }) => {
          const grouped = Boolean(floor && floor.id !== defaultFloorId)
          return (
          <div key={floor?.id ?? 'unfiled'} className="mt-1 first:mt-0">
            {grouped && floor && (
              <div
                className="flex items-center gap-1 px-2 pb-1 pt-2 data-[qa-drop-active=true]:border-t-2 data-[qa-drop-active=true]:border-primary"
                data-qa-group-row={floor.id}
                data-qa-drop={`level:${floor.id}`}
                onContextMenu={onOpenLevelMenu ? (event) => {
                  event.preventDefault()
                  onOpenLevelMenu(floor, event)
                } : undefined}
              >
                <input
                  type="checkbox"
                  data-testid={`qa-level-select-${floor.id}`}
                  aria-label={`Select ${floor.floor_name}`}
                  className="mr-1.5 h-3.5 w-3.5 shrink-0 rounded border-border"
                  checked={levelAreas.length === 0
                    ? selection.isLevelSelected(floor.id)
                    : levelAreas.every((area) => selection.isSelected('areas', area.id))}
                  ref={(node) => {
                    if (!node) return
                    const picked = levelAreas.filter((area) => selection.isSelected('areas', area.id)).length
                    node.indeterminate = picked > 0 && picked < levelAreas.length
                  }}
                  onChange={() => {
                    if (levelAreas.length === 0) {
                      selection.toggleLevel(floor.id)
                      return
                    }
                    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaSelection.ts (toggle)
                    const allOn = levelAreas.every((area) => selection.isSelected('areas', area.id))
                    for (const area of levelAreas) {
                      const on = selection.isSelected('areas', area.id)
                      if (allOn ? on : !on) selection.toggle('areas', area.id)
                    }
                  }}
                  onClick={(event) => event.stopPropagation()}
                />
                <QaDragHandle kind="level" id={floor.id} label={floor.floor_name} />
                {editingGroup === floor.id ? (
                  <input
                    autoFocus
                    defaultValue={floor.floor_name}
                    aria-label="Rename group"
                    className="h-6 min-w-0 flex-1 rounded border border-border bg-background px-1 text-[11px]"
                    onBlur={(event) => {
                      const name = event.target.value.trim()
                      setEditingGroup(null)
                      if (name && name !== floor.floor_name) void controller.api.renameFloor(floor.id, name).then(() => workspace.floorsQuery.refetch())
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur()
                    }}
                  />
                ) : (
                  <button type="button" className="min-w-0 flex-1 truncate text-left text-[11px] font-semibold" onClick={() => setEditingGroup(floor.id)}>
                    {floor.floor_name}
                  </button>
                )}
                <span className="shrink-0 tabular-nums text-[11px] text-muted-foreground">{levelAreas.length}</span>
              </div>
            )}
            {levelAreas.length === 0 ? (
              <p className="px-2 py-1.5 text-[11px] text-muted-foreground/70">
                Empty. Move locations here.
              </p>
            ) : (
              <ul className={grouped ? 'ml-4 space-y-0.5 border-l border-border/60 pl-2' : 'space-y-0.5'}>
                {levelAreas.map((area) => (
                  <AreaRow
                    key={area.id}
                    area={area}
                    active={area.id === openAreaId}
                    renaming={workspace.renameArea.isPending && workspace.renameArea.variables?.id === area.id}
                    onSelect={() => onSelectArea(area.id)}
                    onRename={(name) => workspace.renameArea.mutate({ id: area.id, name })}
                    selection={{
                      selected: selection.isSelected('areas', area.id),
                      onToggle: () => selection.toggle('areas', area.id),
                    }}
                    startRename={startRenameId === area.id}
                    onRenameStarted={onRenameStarted}
                    onOpenMenu={onOpenMenu ? (event) => onOpenMenu(area, event) : undefined}
                    reorder={moves.areaMove(area.id, levelAreas.map((row) => row.id))}
                  />
                ))}
              </ul>
            )}
          </div>
          )
        })
      )}
      <button
        type="button"
        onClick={() => { void addLocation() }}
        disabled={!workspace.activeInspectionId || blankPending}
        className="mt-1 flex w-full items-center gap-2 rounded-lg border border-dashed border-border px-2 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        {blankPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
        {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankArea.ts (addBlankArea) */}
        Add Location
      </button>
    </div>
  )
}
